/**
 * detect.ts — Connected-components sprite detection.
 *
 * Operates on an alpha-keyed ImageData (typically `background.cleaned`).
 * Background is `alpha === 0`; foreground is `alpha >= alphaThreshold`.
 * Returns blobs in reading order (rows of similar Y, left-to-right within row).
 *
 * Pure synchronous TypeScript — no DOM access, safe to import anywhere.
 * Ported from experiments/sprite-detect; the BG-colour-distance step is
 * dropped because the production matte step has already keyed the alpha.
 */

import type { BlobCandidate, Rect } from '~/types/pipeline';

const DEFAULTS = {
  alphaThreshold: 16,
  minArea: 64,
  maxFraction: 0.5,
  fragmentPadding: 0.08,
  outlierFraction: 0.25,
  /** Y-bucket size for reading-order grouping, as a fraction of median bbox height. */
  rowBucketFraction: 0.5,
} as const;

export interface DiscoverOptions {
  alphaThreshold?: number;
  minArea?: number;
  maxFraction?: number;
  fragmentPadding?: number;
  outlierFraction?: number;
}

export function discoverBlobs(img: ImageData, opts: DiscoverOptions = {}): BlobCandidate[] {
  const alphaThreshold = opts.alphaThreshold ?? DEFAULTS.alphaThreshold;
  const minArea = opts.minArea ?? DEFAULTS.minArea;
  const maxFraction = opts.maxFraction ?? DEFAULTS.maxFraction;
  const fragmentPadding = opts.fragmentPadding ?? DEFAULTS.fragmentPadding;
  const outlierFraction = opts.outlierFraction ?? DEFAULTS.outlierFraction;

  const fg = binariseAlpha(img, alphaThreshold);
  const { labels, numLabels } = labelComponents(fg, img.width, img.height);

  const sheetArea = img.width * img.height;
  const filtered = extractComponents(labels, numLabels, img.width, img.height)
    .filter((c) => c.area >= minArea && c.area <= sheetArea * maxFraction)
    .sort((a, b) => b.area - a.area);

  const merged = mergeFragments(filtered, fragmentPadding);
  const sizeFiltered = filterSizeOutliers(merged, outlierFraction);
  const ordered = sortReadingOrder(sizeFiltered);

  return ordered.map((c) => ({
    bbox: c.bbox,
    centroid: centroidOf(c),
    area: c.area,
    mask: c.mask,
  }));
}

// ── Alpha threshold ──────────────────────────────────────────────────────────

function binariseAlpha(img: ImageData, alphaThreshold: number): Uint8Array {
  const { data, width, height } = img;
  const out = new Uint8Array(width * height);
  for (let i = 0, p = 3; i < out.length; i++, p += 4) {
    if ((data[p] ?? 0) >= alphaThreshold) out[i] = 1;
  }
  return out;
}

// ── Two-pass connected components labelling (4-connectivity) ─────────────────

function labelComponents(
  fg: Uint8Array,
  width: number,
  height: number,
): { labels: Int32Array; numLabels: number } {
  const labels = new Int32Array(fg.length);
  const parent: number[] = [0];
  let nextLabel = 1;

  const find = (x: number): number => {
    let r = x;
    while (true) {
      const p = parent[r];
      if (p === undefined || p === r) break;
      r = p;
    }
    let cur = x;
    while (true) {
      const p = parent[cur];
      if (p === undefined || p === r) break;
      parent[cur] = r;
      cur = p;
    }
    return r;
  };
  const union = (a: number, b: number): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[Math.max(ra, rb)] = Math.min(ra, rb);
  };

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      if (fg[i] !== 1) continue;
      const left = x > 0 ? (labels[i - 1] ?? 0) : 0;
      const top = y > 0 ? (labels[i - width] ?? 0) : 0;
      if (left === 0 && top === 0) {
        labels[i] = nextLabel;
        parent[nextLabel] = nextLabel;
        nextLabel++;
      } else if (left !== 0 && top === 0) {
        labels[i] = left;
      } else if (left === 0 && top !== 0) {
        labels[i] = top;
      } else {
        const min = Math.min(left, top);
        labels[i] = min;
        if (left !== top) union(left, top);
      }
    }
  }

  const remap = new Int32Array(nextLabel);
  let outId = 0;
  for (let i = 0; i < labels.length; i++) {
    const l = labels[i] ?? 0;
    if (l === 0) continue;
    const r = find(l);
    if (remap[r] === 0) remap[r] = ++outId;
    labels[i] = remap[r] ?? 0;
  }

  return { labels, numLabels: outId };
}

// ── Component → bbox + cropped mask ──────────────────────────────────────────

interface Component {
  bbox: Rect;
  area: number;
  mask: Uint8Array;
}

function extractComponents(
  labels: Int32Array,
  numLabels: number,
  width: number,
  height: number,
): Component[] {
  if (numLabels === 0) return [];

  const minX = new Int32Array(numLabels + 1).fill(width);
  const minY = new Int32Array(numLabels + 1).fill(height);
  const maxX = new Int32Array(numLabels + 1).fill(-1);
  const maxY = new Int32Array(numLabels + 1).fill(-1);
  const area = new Int32Array(numLabels + 1);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const l = labels[y * width + x] ?? 0;
      if (l === 0) continue;
      if (x < (minX[l] ?? width)) minX[l] = x;
      if (x > (maxX[l] ?? -1)) maxX[l] = x;
      if (y < (minY[l] ?? height)) minY[l] = y;
      if (y > (maxY[l] ?? -1)) maxY[l] = y;
      area[l] = (area[l] ?? 0) + 1;
    }
  }

  const out: Component[] = [];
  for (let l = 1; l <= numLabels; l++) {
    const a = area[l] ?? 0;
    if (a === 0) continue;
    const bx = minX[l];
    const by = minY[l];
    const maxXL = maxX[l];
    const maxYL = maxY[l];
    if (bx === undefined || by === undefined || maxXL === undefined || maxYL === undefined)
      continue;
    const bw = maxXL - bx + 1;
    const bh = maxYL - by + 1;
    const mask = new Uint8Array(bw * bh);
    for (let yy = 0; yy < bh; yy++) {
      const srcRow = (by + yy) * width;
      for (let xx = 0; xx < bw; xx++) {
        if (labels[srcRow + bx + xx] === l) mask[yy * bw + xx] = 1;
      }
    }
    out.push({ bbox: { x: bx, y: by, w: bw, h: bh }, area: a, mask });
  }
  return out;
}

// ── Fragment merge: absorb AA debris inside an inflated bbox ─────────────────

function mergeFragments(components: Component[], paddingFrac: number): Component[] {
  const removed = new Set<number>();
  const work: Component[] = components.map((c) => ({ ...c, mask: c.mask.slice() }));

  for (let i = 0; i < work.length; i++) {
    if (removed.has(i)) continue;
    const initial = work[i];
    if (!initial) continue;
    let big = initial;

    let changed = true;
    while (changed) {
      changed = false;
      const padX = Math.round(big.bbox.w * paddingFrac);
      const padY = Math.round(big.bbox.h * paddingFrac);
      const inflated: Rect = {
        x: big.bbox.x - padX,
        y: big.bbox.y - padY,
        w: big.bbox.w + 2 * padX,
        h: big.bbox.h + 2 * padY,
      };

      for (let j = i + 1; j < work.length; j++) {
        if (removed.has(j)) continue;
        const small = work[j];
        if (!small) continue;
        if (!bboxContains(inflated, small.bbox)) continue;
        big = mergeTwo(big, small);
        work[i] = big;
        removed.add(j);
        changed = true;
      }
    }
  }

  return work.filter((_, i) => !removed.has(i));
}

function filterSizeOutliers(components: Component[], threshold: number): Component[] {
  if (components.length < 3) return components;
  const sortedAreas = [...components].map((c) => c.area).sort((a, b) => a - b);
  const median = sortedAreas[Math.floor(sortedAreas.length / 2)] ?? 0;
  const cutoff = median * threshold;
  return components.filter((c) => c.area >= cutoff);
}

function bboxContains(outer: Rect, inner: Rect): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.w <= outer.x + outer.w &&
    inner.y + inner.h <= outer.y + outer.h
  );
}

function mergeTwo(a: Component, b: Component): Component {
  const x = Math.min(a.bbox.x, b.bbox.x);
  const y = Math.min(a.bbox.y, b.bbox.y);
  const w = Math.max(a.bbox.x + a.bbox.w, b.bbox.x + b.bbox.w) - x;
  const h = Math.max(a.bbox.y + a.bbox.h, b.bbox.y + b.bbox.h) - y;
  const mask = new Uint8Array(w * h);
  paste(mask, w, a.mask, a.bbox.w, a.bbox.h, a.bbox.x - x, a.bbox.y - y);
  paste(mask, w, b.mask, b.bbox.w, b.bbox.h, b.bbox.x - x, b.bbox.y - y);
  return { bbox: { x, y, w, h }, area: a.area + b.area, mask };
}

function paste(
  dst: Uint8Array,
  dstW: number,
  src: Uint8Array,
  srcW: number,
  srcH: number,
  ox: number,
  oy: number,
): void {
  for (let yy = 0; yy < srcH; yy++) {
    for (let xx = 0; xx < srcW; xx++) {
      if (src[yy * srcW + xx] === 1) dst[(oy + yy) * dstW + (ox + xx)] = 1;
    }
  }
}

// ── Reading order ────────────────────────────────────────────────────────────

/** Bucket by Y in rows of (rowBucketFraction × median bbox height), then sort
 *  by X within each row. Stable for sprite sheets where rows are visually distinct. */
function sortReadingOrder(components: Component[]): Component[] {
  if (components.length <= 1) return components;
  const heights = components.map((c) => c.bbox.h).sort((a, b) => a - b);
  const medianH = heights[Math.floor(heights.length / 2)] ?? 1;
  const bucketSize = Math.max(1, Math.round(medianH * DEFAULTS.rowBucketFraction));
  return [...components].sort((a, b) => {
    const aRow = Math.floor(a.bbox.y / bucketSize);
    const bRow = Math.floor(b.bbox.y / bucketSize);
    if (aRow !== bRow) return aRow - bRow;
    return a.bbox.x - b.bbox.x;
  });
}

// ── Centroid ─────────────────────────────────────────────────────────────────

function centroidOf(c: Component): { x: number; y: number } {
  const { bbox, mask } = c;
  let sx = 0;
  let sy = 0;
  let n = 0;
  for (let yy = 0; yy < bbox.h; yy++) {
    for (let xx = 0; xx < bbox.w; xx++) {
      if (mask[yy * bbox.w + xx] === 1) {
        sx += xx;
        sy += yy;
        n++;
      }
    }
  }
  if (n === 0) return { x: bbox.x + bbox.w / 2, y: bbox.y + bbox.h / 2 };
  return { x: bbox.x + sx / n, y: bbox.y + sy / n };
}
